import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import i18n from '../i18n';
import { updateDocumentDirection } from '../utils/languageUtils';

// Define supported languages
export type Language = 'en' | 'he';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Create a single cache instance that we'll reuse
const emotionCache = createCache({
  key: 'css',
  prepend: true,
});

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('he'); // Default to Hebrew
  const isRTL = language === 'he';

  // Create theme based on language direction
  const createLanguageTheme = (isRTL: boolean) => {
    return createTheme({
      direction: isRTL ? 'rtl' : 'ltr',
      palette: {
        mode: 'dark',
        primary: {
          main: '#3B82F6', // Mystic blue
          light: '#60A5FA',
          dark: '#1E40AF',
        },
        secondary: {
          main: '#8B5CF6', // Your original purple
          light: '#A78BFA',
          dark: '#6D28D9',
        },
        warning: {
          main: '#F97316', // Mystical orange
          light: '#FB923C',
          dark: '#EA580C',
        },
        info: {
          main: '#14B8A6', // Teal accent
          light: '#5EEAD4',
        },
        success: {
          main: '#22C55E',
          light: '#4ADE80',
        },
        background: {
          default: '#030712', // Midnight blue
          paper: '#111827',
        },
        text: {
          primary: '#F9FAFB',
          secondary: '#9CA3AF',
        }
      },
      typography: {
        fontFamily: isRTL 
          ? '"Heebo", "Inter", "Roboto", "Helvetica", "Arial", sans-serif'
          : '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
          fontWeight: 700,
        },
        h2: {
          fontWeight: 600,
        },
        h3: {
          fontWeight: 600,
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              direction: isRTL ? 'rtl' : 'ltr',
              backgroundColor: 'hsl(219, 27%, 22%)',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(40, 53, 74, 0.5)',
              borderRadius: '10px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(126, 161, 255, 0.18)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'hsl(225, 73%, 57%)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'hsl(54, 78%, 77%)',
              },
              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                color: 'hsl(54, 78%, 77%)',
              },
            },
            input: {
              color: 'hsl(54, 78%, 77%)',
              '::placeholder': {
                color: 'hsl(54, 78%, 77%)',
                opacity: 1,
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: 'hsl(225, 73%, 57%)',
              '&.Mui-focused': {
                color: 'hsl(54, 78%, 77%)',
              },
            },
          },
        },
      },
    });
  };

  // Update document direction when language changes
  useEffect(() => {
    updateDocumentDirection(isRTL);
  }, [isRTL]);

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    // Change i18n language
    i18n.changeLanguage(newLanguage);
    // Store language preference in localStorage
    localStorage.setItem('preferredLanguage', newLanguage);
  };

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'he')) {
      setLanguage(savedLanguage);
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  const theme = createLanguageTheme(isRTL);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleLanguageChange, isRTL }}>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </CacheProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 